/**
 * PixiEntityRenderer.js - WebGL Entity Rendering
 *
 * Handles rendering of game entities using PixiJS:
 * - Players (sprites, weapons, effects)
 * - Projectiles (with object pooling)
 */

import * as PIXI from 'pixi.js';
import { positionAt } from '../../core/ProjectileSim.js';
import { resolveAbilityView, applyAbilityEffects } from './abilityEffects.js';
import { WeaponFireMode } from '../../../shared/Weapon.js';
import { setGroupAlpha } from './groupAlpha.js';
import { deathEffectFrame, makeDeathShards } from './deathEffect.js';
import { resolveBackpack, backpackLayout } from '../../core/backpacks.js';

// Half-arc of a melee swing, in radians (45° each side of center = 90° total sweep).
const MELEE_SWING_HALF_ARC = Math.PI / 4;

const PixiEntityRenderer = {
  // Container references
  playersContainer: null,
  projectilesContainer: null,

  // Player sprite management (Map: playerId -> PlayerSpriteGroup)
  playerSprites: new Map(),
  // Decoys get their own map but the same sprite groups: a decoy that rendered
  // through a second, "decoy-ish" path would be spottable, which is the whole
  // thing it must not be.
  decoySprites: new Map(),
  // Sprite groups of players who just died, playing out their death beat
  // (deathEffect.js) before being destroyed.
  dyingSprites: [],

  // Projectile sprite pool
  projectilePool: [],
  activeProjectiles: new Map(), // projectileId -> sprite
  projectileTextureCache: new Map(), // spritePath -> PIXI.Texture

  // Player rendering constants
  PLAYER_RADIUS: 40,
  SHADOW_RADIUS: 40,

  // Texture cache
  textureCache: new Map(),

  /**
   * Initialize the entity renderer
   * @param {PIXI.Container} playersContainer - Container for players
   * @param {PIXI.Container} projectilesContainer - Container for projectiles
   */
  init(playersContainer, projectilesContainer) {
    this.playersContainer = playersContainer;
    this.projectilesContainer = projectilesContainer;

    // Pre-create projectile pool
    this._initProjectilePool(100);
  },

  /**
   * Initialize projectile object pool
   * @private
   */
  _initProjectilePool(size) {
    for (let i = 0; i < size; i++) {
      const sprite = this._createProjectileSprite();
      sprite.visible = false;
      this.projectilesContainer.addChild(sprite);
      this.projectilePool.push(sprite);
    }
  },

  /**
   * Create a projectile sprite
   * @private
   */
  _createProjectileSprite() {
    // Create a simple circle for now (can be replaced with texture)
    // PixiJS 8.x API
    const g = new PIXI.Graphics();
    g.circle(0, 0, 4);
    g.fill({ color: 0xffff00 });
    return g;
  },

  /**
   * Get a projectile from the pool
   * @private
   */
  _getProjectileFromPool() {
    let sprite = this.projectilePool.pop();
    if (!sprite) {
      // Pool exhausted, create new sprite
      sprite = this._createProjectileSprite();
      this.projectilesContainer.addChild(sprite);
    }
    sprite.visible = true;
    return sprite;
  },

  /**
   * Return a projectile to the pool
   * @private
   */
  _returnProjectileToPool(sprite) {
    sprite.visible = false;
    this.projectilePool.push(sprite);
  },

  /**
   * Update all players
   * @param {number} renderTime - Interpolated render time
   * @param {GameState} gameState - Game state reference
   * @param {Object} callbacks - Callback functions
   */
  updatePlayers(renderTime, gameState, callbacks) {
    const now = performance.now();
    const { getSkinTexture, getWeaponTexture, getPlayerLastShotTime } = callbacks;

    // Track which players are still alive
    const alivePlayers = new Set();

    for (const player of gameState.players.values()) {
      // Skip dead players
      if (player.isDead) continue;

      alivePlayers.add(player.id);
      const renderState = player.getRenderState(renderTime);

      // Get or create sprite group for this player
      let spriteGroup = this.playerSprites.get(player.id);
      if (!spriteGroup) {
        spriteGroup = this._createPlayerSpriteGroup(player, callbacks);
        this.playerSprites.set(player.id, spriteGroup);
      }

      // Update sprite group
      this._updatePlayerSpriteGroup(spriteGroup, player, renderState, now, {
        ...callbacks,
        gameState,
        teamColorOf: (team) => (typeof gameState.teamColor === 'function' ? gameState.teamColor(team) : null)
      });
    }

    // Remove sprites for dead/disconnected players. A death plays its beat
    // first; a disconnect just goes.
    for (const [playerId, spriteGroup] of this.playerSprites) {
      if (!alivePlayers.has(playerId)) {
        // Dead players in no-respawn modes may already be gone from the map.
        if (gameState.players.get(playerId)?.isDead || gameState.recentDeaths?.has(playerId)) {
          this._startDeathEffect(spriteGroup, now);
        } else {
          this._destroyPlayerSpriteGroup(spriteGroup);
        }
        this.playerSprites.delete(playerId);
      }
    }

    this._updateDeathEffects(now);
  },

  /**
   * Hand a dead player's sprite group over to the death beat. It stays where
   * the player fell; everything but the body, shadow and team ring is hidden.
   * @private
   */
  _startDeathEffect(group, now) {
    // An invisible player's death should not reveal where they were standing.
    if (!group.container.visible) {
      this._destroyPlayerSpriteGroup(group);
      return;
    }
    group.reloadBarContainer.visible = false;
    group.muzzleDebug.visible = false;
    if (group.worldOverlay) group.worldOverlay.visible = false;

    const flash = new PIXI.Graphics();
    flash.circle(0, 0, this.PLAYER_RADIUS);
    flash.fill({ color: 0xffffff });
    flash.blendMode = 'add';
    group.container.addChild(flash);

    const burst = new PIXI.Graphics();
    group.container.addChild(burst);

    this.dyingSprites.push({
      group,
      flash,
      burst,
      startedAt: now,
      baseRotation: group.rotationContainer.rotation,
      shards: makeDeathShards(),
      color: group.currentTeamColor ?? 0xff5a3c
    });
  },

  /**
   * Advance every playing death beat; destroy the ones that have finished.
   * @private
   */
  _updateDeathEffects(now) {
    if (this.dyingSprites.length === 0) return;
    const stillDying = [];
    for (const dying of this.dyingSprites) {
      const f = deathEffectFrame(now - dying.startedAt);
      if (f.done) {
        this._destroyPlayerSpriteGroup(dying.group);
        continue;
      }
      stillDying.push(dying);

      const { group, flash, burst } = dying;
      group.rotationContainer.scale.set(f.bodyScale);
      group.rotationContainer.rotation = dying.baseRotation + f.spin;
      setGroupAlpha(group.rotationContainer, f.bodyAlpha, () => new PIXI.AlphaFilter());
      group.shadow.alpha = 0.35 * f.bodyAlpha;
      group.teamRing.alpha = f.bodyAlpha;

      flash.scale.set(f.bodyScale);
      flash.alpha = f.flashAlpha;

      burst.clear();
      burst.circle(0, 0, f.ringRadius);
      burst.stroke({ width: 4, color: 0xffffff, alpha: f.ringAlpha });
      for (const shard of dying.shards) {
        const d = f.shardDistance * shard.speed;
        burst.circle(Math.cos(shard.angle) * d, Math.sin(shard.angle) * d, f.shardSize);
      }
      burst.fill({ color: dying.color, alpha: f.shardAlpha });
    }
    this.dyingSprites = stillDying;
  },

  /**
   * Update all decoys — deliberately the same body as updatePlayers over a
   * different map. A decoy has no death state (it is removed, not killed), so
   * there is no isDead check; everything else is a player, exactly. One that
   * was shot plays the player death beat, so onlookers read it as a kill.
   * @param {number} renderTime - Interpolated render time
   * @param {GameState} gameState - Game state reference
   * @param {Object} callbacks - Callback functions
   */
  updateDecoys(renderTime, gameState, callbacks) {
    const now = performance.now();
    const present = new Set();

    for (const decoy of (gameState.decoys?.values?.() ?? [])) {
      present.add(decoy.id);
      const renderState = decoy.getRenderState(renderTime);

      let spriteGroup = this.decoySprites.get(decoy.id);
      if (!spriteGroup) {
        spriteGroup = this._createPlayerSpriteGroup(decoy, callbacks);
        this.decoySprites.set(decoy.id, spriteGroup);
      }

      this._updatePlayerSpriteGroup(spriteGroup, decoy, renderState, now, {
        ...callbacks,
        gameState,
        teamColorOf: (team) => (typeof gameState.teamColor === 'function' ? gameState.teamColor(team) : null)
      });
    }

    for (const [decoyId, spriteGroup] of this.decoySprites) {
      if (!present.has(decoyId)) {
        if (gameState.recentDecoyShots?.has(decoyId)) {
          this._startDeathEffect(spriteGroup, now);
        } else {
          this._destroyPlayerSpriteGroup(spriteGroup);
        }
        this.decoySprites.delete(decoyId);
      }
    }
  },

  /**
   * Create a sprite group for a player
   * @private
   */
  _createPlayerSpriteGroup(player, callbacks) {
    const { getSkinTexture } = callbacks;

    // Container for all player visuals
    const container = new PIXI.Container();
    this.playersContainer.addChild(container);

    // Shadow (drawn first, below player) - PixiJS 8.x API
    const shadow = new PIXI.Graphics();
    shadow.circle(0, 0, this.SHADOW_RADIUS);
    shadow.fill({ color: 0x000000, alpha: 0.35 });
    container.addChild(shadow);

    // Team ring (Phase 3B): drawn under the body, coloured per team, hidden in non-team modes
    const teamRing = new PIXI.Graphics();
    teamRing.visible = false;
    container.addChild(teamRing);

    // Player sprite container (for rotation)
    const rotationContainer = new PIXI.Container();
    container.addChild(rotationContainer);

    // Backpack (drawn first, under everything, so the body covers its top
    // half). Texture and placement are set by _updateBackpackSprite.
    const backpackSprite = new PIXI.Sprite();
    backpackSprite.anchor.set(0.5, 0.5);
    backpackSprite.visible = false;
    rotationContainer.addChild(backpackSprite);

    // Weapon sprite (drawn below player)
    const weaponSprite = new PIXI.Sprite();
    weaponSprite.anchor.set(0, 0.5);
    rotationContainer.addChild(weaponSprite);

    // Player body sprite
    const skinId = player.cosmetics?.equippedSkin || 'default';
    const texture = getSkinTexture ? getSkinTexture(skinId) : null;
    const bodySprite = texture ? new PIXI.Sprite(texture) : new PIXI.Graphics();

    if (texture) {
      bodySprite.anchor.set(0.5, 0.5);
      bodySprite.width = this.PLAYER_RADIUS * 2;
      bodySprite.height = this.PLAYER_RADIUS * 2;
    } else {
      // Fallback: draw colored circle - PixiJS 8.x API
      bodySprite.circle(0, 0, this.PLAYER_RADIUS);
      bodySprite.fill({ color: 0x00ff00 });
    }
    rotationContainer.addChild(bodySprite);

    // Invulnerability effect overlay
    const invulnOverlay = new PIXI.Graphics();
    invulnOverlay.visible = false;
    rotationContainer.addChild(invulnOverlay);

    // World-space overlay for ability visuals that are not attached to the body
    // (e.g. the rewind marker, which sits where the player will snap back to).
    const worldOverlay = new PIXI.Graphics();
    worldOverlay.visible = false;
    this.playersContainer.addChild(worldOverlay);

    // Reload progress bar (positioned below player, doesn't rotate)
    const reloadBarContainer = new PIXI.Container();
    reloadBarContainer.y = 100; // 100px below player center
    reloadBarContainer.visible = false;
    container.addChild(reloadBarContainer);

    // Reload bar background
    const reloadBarBg = new PIXI.Graphics();
    const barWidth = 60;
    const barHeight = 8;
    reloadBarBg.roundRect(-barWidth / 2, -barHeight / 2, barWidth, barHeight, 3);
    reloadBarBg.fill({ color: 0x000000, alpha: 0.6 });
    reloadBarBg.stroke({ width: 1, color: 0x333333 });
    reloadBarContainer.addChild(reloadBarBg);

    // Reload bar fill (will be redrawn each frame based on progress)
    const reloadBarFill = new PIXI.Graphics();
    reloadBarContainer.addChild(reloadBarFill);

    // Reload text with animated dots
    const reloadText = new PIXI.Text({
      text: 'Reloading...',
      style: {
        fontFamily: 'Arial',
        fontSize: 12,
        fill: 0xffffff,
        align: 'center'
      }
    });
    reloadText.anchor.set(0.5, 0);
    reloadText.y = barHeight / 2 + 4; // Below the bar
    reloadBarContainer.addChild(reloadText);

    // Muzzle debug visualization (positioned in world space, not rotated with player)
    const muzzleDebug = new PIXI.Graphics();
    muzzleDebug.visible = false;
    container.addChild(muzzleDebug);

    return {
      container,
      shadow,
      teamRing,
      rotationContainer,
      bodySprite,
      backpackSprite,
      weaponSprite,
      invulnOverlay,
      worldOverlay,
      reloadBarContainer,
      reloadBarFill,
      reloadText,
      muzzleDebug,
      currentSkinId: skinId,
      currentBackpackId: undefined,   // never a real id, so the first update always resolves it
      currentTeamColor: null
    };
  },

  /**
   * Update a player sprite group
   * @private
   */
  _updatePlayerSpriteGroup(group, player, renderState, now, callbacks) {
    const { getSkinTexture, getWeaponTexture, getPlayerLastShotTime, debugMuzzleOffset } = callbacks;

    // Ability visuals come from the registry (abilityEffects.js), not from
    // per-ability branches here.
    const view = resolveAbilityView(player, { now, group });
    if (!view.visible) {
      group.container.visible = false;
      return;
    }

    group.container.visible = true;
    const opacity = view.alpha;

    // Update position
    group.container.x = renderState.x;
    group.container.y = renderState.y;

    // Update shadow alpha
    group.shadow.alpha = 0.35 * opacity;

    // Team ring: redraw only when the colour changes (dirty flag, not per frame)
    const teamColor = callbacks.teamColorOf ? callbacks.teamColorOf(player.team) : null;
    if (teamColor !== group.currentTeamColor) {
      group.teamRing.clear();
      if (teamColor !== null) {
        group.teamRing.circle(0, 0, this.SHADOW_RADIUS + 4);
        group.teamRing.stroke({ width: 4, color: teamColor, alpha: 0.9 });
      }
      group.teamRing.visible = teamColor !== null;
      group.currentTeamColor = teamColor;
    }
    group.teamRing.alpha = opacity;

    // Update rotation (add PI/2 to match Canvas2D orientation)
    group.rotationContainer.rotation = renderState.angle + Math.PI / 2;
    // Fade body + weapon as one flattened image; a plain .alpha would fade
    // each child separately and the weapon (under the body) would show through.
    setGroupAlpha(group.rotationContainer, opacity, () => new PIXI.AlphaFilter());

    // Update skin if changed
    const skinId = player.cosmetics?.equippedSkin || 'default';
    if (skinId !== group.currentSkinId) {
      const texture = getSkinTexture ? getSkinTexture(skinId) : null;
      if (texture && group.bodySprite instanceof PIXI.Sprite) {
        group.bodySprite.texture = texture;
      }
      group.currentSkinId = skinId;
    }

    this._updateBackpackSprite(group, player, callbacks);

    // Update weapon
    this._updateWeaponSprite(group, player, now, callbacks);

    // Per-frame ability effects (invulnerability ring, …); ends fire once
    applyAbilityEffects(group, player, { now, alpha: opacity, player });

    // Update reload progress bar
    this._updateReloadBar(group, player, now, callbacks);

    // Update muzzle debug visualization
    this._updateMuzzleDebug(group, player, renderState, debugMuzzleOffset);
  },

  /**
   * Point the backpack sprite at the player's equipped backpack. Only runs
   * when the id changes; 'none' (or missing art) hides the sprite.
   * @private
   */
  _updateBackpackSprite(group, player, { getBackpackTexture, gameState } = {}) {
    const backpackId = player.cosmetics?.equippedBackpack ?? null;
    if (backpackId === group.currentBackpackId) return;
    group.currentBackpackId = backpackId;

    const sprite = group.backpackSprite;
    const entry = resolveBackpack(backpackId, gameState?.cosmeticsConfig);
    const texture = entry && getBackpackTexture ? getBackpackTexture(backpackId) : null;
    if (!texture) {
      sprite.visible = false;
      return;
    }
    const { width, height, offsetY } = backpackLayout(entry);
    sprite.texture = texture;
    sprite.width = width;
    sprite.height = height;
    sprite.y = offsetY;
    sprite.visible = true;
  },

  /**
   * Update reload progress bar for a player
   * @private
   */
  _updateReloadBar(group, player, now, ctx = null) {
    if (!player.isLocal) {
      group.reloadBarContainer.visible = false;
      return;
    }

    // One bar, two things that take time: reloading a weapon and using an
    // item. Whichever is running drives it.
    const progressState = this._resolveProgressBarState(player, ctx);
    if (!progressState) {
      group.reloadBarContainer.visible = false;
      return;
    }

    const { startedAt, duration, label, clock } = progressState;
    // Reload timestamps are on the simulation clock; item-use timestamps are
    // local wall time (stamped when the socket event arrives). Mixing them
    // makes a sniper reload bar fill before the server finishes.
    const clockNow = clock === 'server' && typeof ctx?.gameState?.serverNow === 'function'
      ? ctx.gameState.serverNow()
      : Date.now();
    const elapsed = clockNow - startedAt;
    const progress = Math.min(1, Math.max(0, elapsed / duration));

    // Show the bar
    group.reloadBarContainer.visible = true;

    // Redraw fill bar based on progress
    const barWidth = 60;
    const barHeight = 8;
    const fillWidth = (barWidth - 4) * progress;

    group.reloadBarFill.clear();
    if (fillWidth > 0) {
      group.reloadBarFill.roundRect(-barWidth / 2 + 2, -barHeight / 2 + 2, fillWidth, barHeight - 4, 2);
      group.reloadBarFill.fill({ color: 0xffffff });
    }

    // Animate dots (cycle through 1-3 dots every 400ms)
    const dotCount = Math.floor((now / 400) % 3) + 1;
    group.reloadText.text = label + '.'.repeat(dotCount);
  },

  /**
   * What the progress bar should show, or null for nothing in progress.
   * An item use wins over a reload: a use cancels on almost anything, so while
   * one is running it is the thing the player is waiting on.
   * @returns {{ startedAt: number, duration: number, label: string, clock: 'wall'|'server' }|null}
   * @private
   */
  _resolveProgressBarState(player, ctx) {
    const use = ctx?.gameState?.itemUses?.get(player.id);
    if (use && use.completesAt > use.startedAt) {
      return { startedAt: use.startedAt, duration: use.completesAt - use.startedAt, label: 'Using', clock: 'wall' };
    }

    const weaponState = player.weaponState;
    if (weaponState?.reloading && weaponState.reloadStartTime && weaponState.reloadTime) {
      return {
        startedAt: weaponState.reloadStartTime,
        duration: weaponState.reloadTime,
        label: 'Reloading',
        clock: 'server'
      };
    }

    return null;
  },

  /**
   * Update muzzle debug visualization for a player
   * @private
   */
  _updateMuzzleDebug(group, player, renderState, debugMuzzleOffset) {
    if (!debugMuzzleOffset || !player.equippedWeapon?.muzzleOffset) {
      group.muzzleDebug.visible = false;
      return;
    }

    const weapon = player.equippedWeapon;
    const muzzle = weapon.muzzleOffset;

    // Calculate muzzle position relative to player (in local container space)
    const cos = Math.cos(renderState.angle);
    const sin = Math.sin(renderState.angle);
    const muzzleX = muzzle.x * cos - muzzle.y * sin;
    const muzzleY = muzzle.x * sin + muzzle.y * cos;

    // Draw debug visualization
    group.muzzleDebug.visible = true;
    group.muzzleDebug.clear();

    // Draw crosshair
    const crosshairSize = 8;
    group.muzzleDebug.moveTo(muzzleX - crosshairSize, muzzleY);
    group.muzzleDebug.lineTo(muzzleX + crosshairSize, muzzleY);
    group.muzzleDebug.moveTo(muzzleX, muzzleY - crosshairSize);
    group.muzzleDebug.lineTo(muzzleX, muzzleY + crosshairSize);
    group.muzzleDebug.stroke({ width: 2, color: 0xff0000 });

    // Draw small circle at exact muzzle point
    group.muzzleDebug.circle(muzzleX, muzzleY, 3);
    group.muzzleDebug.fill({ color: 0xff0000 });
  },

  /**
   * Update weapon sprite for a player
   * @private
   */
  _updateWeaponSprite(group, player, now, callbacks) {
    const { getWeaponTexture, getPlayerLastShotTime } = callbacks;
    const weapon = player.equippedWeapon;

    if (!weapon) {
      group.weaponSprite.visible = false;
      return;
    }

    // Determine weapon state
    const lastShotTime = getPlayerLastShotTime ? getPlayerLastShotTime(player.id) : null;
    const timeSinceShot = lastShotTime ? now - lastShotTime : Infinity;
    const isFiring = timeSinceShot < (weapon.firingStateDuration || 200);
    const isMelee = weapon.fireMode === WeaponFireMode.MELEE;

    // Get appropriate sprite path
    const spritePath = isFiring
      ? (weapon.weaponSpriteFiring || weapon.weaponSprite)
      : (weapon.weaponSpriteIdle || weapon.weaponSprite);

    // Melee weapons swing on a fixed grip point (see rotation below) instead
    // of shifting position on hit, so they always use the idle grip offset.
    const gripOffset = (isFiring && !isMelee)
      ? (weapon.gripOffsetFiring || weapon.gripOffset || { x: -10, y: 0 })
      : (weapon.gripOffsetIdle || weapon.gripOffset || { x: -10, y: 0 });

    // Get texture
    const texture = getWeaponTexture ? getWeaponTexture(spritePath) : null;
    if (!texture) {
      group.weaponSprite.visible = false;
      return;
    }

    group.weaponSprite.visible = true;
    group.weaponSprite.texture = texture;
    group.weaponSprite.x = gripOffset.x;
    group.weaponSprite.y = gripOffset.y;
    group.weaponSprite.width = weapon.weaponWidth;
    group.weaponSprite.height = weapon.weaponHeight;

    // Melee weapons swing in an arc around their grip point: rest left of
    // center, sweep to the same distance right of center on a hit, and
    // sweep back to rest — a full cycle each swing, rather than snapping
    // sideways. Anchor is (0, 0.5) so rotation pivots at the grip, not the
    // sprite's midpoint.
    if (isMelee) {
      const swingDuration = weapon.firingStateDuration || 200;
      const progress = Math.min(1, timeSinceShot / swingDuration);
      group.weaponSprite.rotation = isFiring
        ? -MELEE_SWING_HALF_ARC * Math.cos(2 * Math.PI * progress)
        : -MELEE_SWING_HALF_ARC;
    } else {
      group.weaponSprite.rotation = 0;
    }

    // Handle vertical flip
    if (weapon.flipVertical) {
      group.weaponSprite.scale.y = -Math.abs(group.weaponSprite.scale.y);
    } else {
      group.weaponSprite.scale.y = Math.abs(group.weaponSprite.scale.y);
    }
  },

  /**
   * Destroy a player sprite group
   * @private
   */
  _destroyPlayerSpriteGroup(group) {
    this.playersContainer.removeChild(group.container);
    group.container.destroy({ children: true });
    if (group.worldOverlay) {
      this.playersContainer.removeChild(group.worldOverlay);
      group.worldOverlay.destroy();
    }
  },

  /**
   * Update projectiles
   * @param {GameState} gameState - Game state reference
   * @param {Object} callbacks - Callback functions
   */
  updateProjectiles(gameState, callbacks) {
    // Which projectiles exist is decided by core/ProjectileSim (client update
    // tick); here we only place sprites at the frame-time position.
    const now = performance.now();
    const activeIds = new Set();

    for (const { key, projectile, origin } of gameState.visibleProjectiles || []) {
      const { x, y } = positionAt(origin, now);
      activeIds.add(key);
      this._updateProjectileSprite(key, x, y, projectile, origin, callbacks);
    }

    // Return inactive projectiles to pool (or destroy texture sprites)
    for (const [id, sprite] of this.activeProjectiles) {
      if (!activeIds.has(id)) {
        if (sprite._isTextureSprite) {
          // Texture sprites are destroyed, not pooled
          this.projectilesContainer.removeChild(sprite);
          sprite.destroy();
        } else {
          // Graphics sprites go back to pool
          this._returnProjectileToPool(sprite);
        }
        this.activeProjectiles.delete(id);
      }
    }
  },

  /**
   * Update or create a projectile sprite
   * @private
   */
  _updateProjectileSprite(id, x, y, projectile, state, callbacks) {
    let sprite = this.activeProjectiles.get(id);
    const { getProjectileSprite } = callbacks || {};

    // Check if we have a loaded image for this projectile's sprite
    const img = projectile.sprite && getProjectileSprite ? getProjectileSprite(projectile.sprite) : null;
    const hasValidImage = img && img.complete && img.naturalWidth > 0;

    if (!sprite) {
      if (hasValidImage) {
        // Create texture from image if not cached
        if (!this.projectileTextureCache.has(projectile.sprite)) {
          const texture = PIXI.Texture.from(img);
          this.projectileTextureCache.set(projectile.sprite, texture);
        }
        const texture = this.projectileTextureCache.get(projectile.sprite);
        sprite = new PIXI.Sprite(texture);
        sprite.anchor.set(0.5, 0.5);
        sprite.width = projectile.spriteWidth || 16;
        sprite.height = projectile.spriteHeight || 8;
        sprite._isTextureSprite = true;
        this.projectilesContainer.addChild(sprite);
      } else {
        // Fallback to pool (Graphics circles)
        sprite = this._getProjectileFromPool();
        sprite._isTextureSprite = false;

        // Update color based on projectile - PixiJS 8.x API
        if (sprite instanceof PIXI.Graphics) {
          sprite.clear();
          const color = this._parseColor(projectile.tracerColor || '#ffff00');
          sprite.circle(0, 0, projectile.projectileSize || 4);
          sprite.fill({ color: color });
        }
      }
      this.activeProjectiles.set(id, sprite);
    }

    sprite.x = x;
    sprite.y = y;

    // Handle rotation for rotating projectiles
    if (projectile.rotates) {
      sprite.rotation = projectile.angle || Math.atan2(state.vy, state.vx);
    }
  },

  /**
   * Parse hex color string to number
   * @private
   */
  _parseColor(colorStr) {
    if (typeof colorStr === 'number') return colorStr;
    if (colorStr.startsWith('#')) {
      return parseInt(colorStr.slice(1), 16);
    }
    return 0xffff00; // Default yellow
  },

  /**
   * Drop any death beats still playing (lobby return / teardown).
   * @private
   */
  _clearDeathEffects() {
    for (const dying of this.dyingSprites) {
      this._destroyPlayerSpriteGroup(dying.group);
    }
    this.dyingSprites = [];
  },

  /**
   * Reset renderer state without destroying containers
   * Called when returning to lobby to clear cached sprites
   */
  reset() {
    this._clearDeathEffects();

    // Clear player sprites
    for (const group of this.playerSprites.values()) {
      this._destroyPlayerSpriteGroup(group);
    }
    this.playerSprites.clear();

    for (const group of this.decoySprites.values()) {
      this._destroyPlayerSpriteGroup(group);
    }
    this.decoySprites.clear();

    // Clear projectile sprites
    for (const sprite of this.activeProjectiles.values()) {
      if (sprite._isTextureSprite) {
        this.projectilesContainer.removeChild(sprite);
        sprite.destroy();
      } else {
        this._returnProjectileToPool(sprite);
      }
    }
    this.activeProjectiles.clear();
  },

  /**
   * Clean up resources
   */
  destroy() {
    this._clearDeathEffects();

    for (const group of this.playerSprites.values()) {
      this._destroyPlayerSpriteGroup(group);
    }
    this.playerSprites.clear();

    for (const group of this.decoySprites.values()) {
      this._destroyPlayerSpriteGroup(group);
    }
    this.decoySprites.clear();

    for (const sprite of this.activeProjectiles.values()) {
      sprite.destroy();
    }
    this.activeProjectiles.clear();

    for (const sprite of this.projectilePool) {
      sprite.destroy();
    }
    this.projectilePool = [];
  }
};

// Export for use in other modules

export { PixiEntityRenderer };
